/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { litellmRevokeInstance } from "../funcs/litellmRevokeInstance.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RevokeLiteLLMInstanceRequest,
  RevokeLiteLLMInstanceSecurity,
} from "../models/operations/revokelitellminstance.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RevokeLiteLLMInstanceMutationVariables = {
  request: RevokeLiteLLMInstanceRequest;
  security?: RevokeLiteLLMInstanceSecurity | undefined;
  options?: RequestOptions;
};

export type RevokeLiteLLMInstanceMutationData = void;

export type RevokeLiteLLMInstanceMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * revokeInstance litellm
 *
 * @remarks
 * Revoke a LiteLLM integration and immediately invalidate its active key. Requires org:admin.
 */
export function useRevokeLiteLLMInstanceMutation(
  options?: MutationHookOptions<
    RevokeLiteLLMInstanceMutationData,
    RevokeLiteLLMInstanceMutationError,
    RevokeLiteLLMInstanceMutationVariables
  >,
): UseMutationResult<
  RevokeLiteLLMInstanceMutationData,
  RevokeLiteLLMInstanceMutationError,
  RevokeLiteLLMInstanceMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRevokeLiteLLMInstanceMutation(client, options),
    ...options,
  });
}

export function mutationKeyRevokeLiteLLMInstance(): MutationKey {
  return ["@gram/client", "litellm", "revokeInstance"];
}

export function buildRevokeLiteLLMInstanceMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RevokeLiteLLMInstanceMutationVariables,
  ) => Promise<RevokeLiteLLMInstanceMutationData>;
} {
  return {
    mutationKey: mutationKeyRevokeLiteLLMInstance(),
    mutationFn: function revokeLiteLLMInstanceMutationFn({
      request,
      security,
      options,
    }): Promise<RevokeLiteLLMInstanceMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(litellmRevokeInstance(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
